import React, { useContext, useCallback, useMemo } from "react";
import clsx from "clsx";
import fp from "lodash/fp";
import { Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Field, change, touch, getFormValues } from "redux-form";

import { Skeleton, RequiredMark, CustomInput } from "components";
import {
	ApplicationEntryProgressContext,
	RequiredMarkContext,
} from "components/contexts";
import { DebounceTimeout } from "globalConstants";
import { useMccCodes } from "queries";
import { trimMidSentenceNormalizer, trimNormalizer } from "validators";
import {
	applyValidatorsIf,
	briefDescriptionNormalizer,
	normalizeAll,
	required,
} from "validators/validation";

import { SelectAsyncForMccCodeSection } from "../select/selectAsyncForMccCodeSection";

import styles from "./formSections.module.scss";

const MccCodeSection = ({
	formName,
	sectionName = "mccCodeSection",
	industryTypeName,
	mccCode,
	mccAliasId = "",
	industryType,
	allLoading,
	hideRequiredMark,
	isPaperApplication,
	isDescriptionRequired = true,
}) => {
	const dispatch = useDispatch();
	const isRequired = useContext(RequiredMarkContext);
	const showRequiredMark =
		useContext(RequiredMarkContext) && !hideRequiredMark;
	const { triggerSave } = useContext(ApplicationEntryProgressContext);

	const valueAlias =
		industryType && mccCode
			? { value: mccCode, label: industryType, id: mccAliasId }
			: null;

	const { loadMccCodes } = useMccCodes();

	const mccCodeUnknown = useSelector(
		fp.flow(
			getFormValues(formName),
			fp.get(sectionName),
			fp.get("mccCodeUnknown"),
		),
	);

	const handleMccCodeChange = option => {
		dispatch(
			change(
				formName,
				`${sectionName}.${industryTypeName}`,
				option.label,
			),
		);
		dispatch(change(formName, `${sectionName}.mccCode`, option.value));
		dispatch(change(formName, `${sectionName}.mccAliasId`, option.id));
	};

	const handleTouchIndustryType = () => {
		dispatch(touch(formName, `${sectionName}.${industryTypeName}`));
		triggerSave?.();
	};

	const MccCodeFieldWithLoader = useCallback(
		() => (
			<div
				className={clsx(styles.mccCode, mccCodeUnknown && "text-muted")}
			>
				<span className={styles.mccCodeLabel}>MCC Code - </span>
				{allLoading ? (
					<Skeleton variant="text" width="36px" />
				) : (
					<span>{mccCode}</span>
				)}
			</div>
		),
		[allLoading, mccCode, mccCodeUnknown],
	);

	const validateMccSectionRequired = useMemo(() => {
		const validator = applyValidatorsIf(
			() => !mccCodeUnknown && !isPaperApplication,
		)(isRequired ? [required] : undefined);
		return validator;
	}, [isPaperApplication, isRequired, mccCodeUnknown]);

	return (
		<>
			<div className="mb-3">
				What type of business is this?
				{showRequiredMark && !isPaperApplication && <RequiredMark />}
			</div>
			<Row>
				<Col lg={5} md={12}>
					<Row className={styles.wrappMccCode}>
						<Col sm={6} className={styles.wrappMccCodeAutocomplete}>
							<Field
								component={SelectAsyncForMccCodeSection}
								cacheOptions
								debounceTimeout={DebounceTimeout}
								loadOptions={loadMccCodes}
								placeholder="Enter MCC description"
								name={industryTypeName}
								additional={{ page: 0 }}
								validate={validateMccSectionRequired}
								blurLoading={allLoading}
								valueAlias={valueAlias}
								onChangeAlias={handleMccCodeChange}
								onBlurAlias={handleTouchIndustryType}
								disabled={mccCodeUnknown}
								isOptionSelected={o => {
									return (
										o.value === mccCode &&
										o.label === industryType
									);
								}}
							/>
						</Col>
					</Row>
					<Row style={{ marginBottom: 30 }}>
						<Col sm={6}>
							<MccCodeFieldWithLoader />
						</Col>
					</Row>
				</Col>
			</Row>
			<Field
				component={CustomInput}
				as="textarea"
				wrapperClassName={"flex-column"}
				label="Give a brief description of the products sold and/or services provided"
				name="detailedExplanationWhatDoYouSell"
				normalizeOnBlur={trimNormalizer}
				normalize={normalizeAll([
					trimMidSentenceNormalizer,
					briefDescriptionNormalizer,
				])}
				validate={isDescriptionRequired ? required : undefined}
			/>
		</>
	);
};

export default MccCodeSection;
